import { ElementDefinition } from "cytoscape";
import { useEffect, useState } from "react";
import { CiZoomIn, CiZoomOut } from "react-icons/ci";
import { MdOutlineZoomInMap } from "react-icons/md";
import "./network-graph.scss";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import fcose from "cytoscape-fcose";

export interface INetworkGraphNode {
  data: {
    id: string;
    label: string;
    type: string;
  };
}

export interface INetworkGraphEdge {
  data: {
    source: string;
    target: string;
    label: string;
  };
}

interface INetworkGraphProps {
  nodes: INetworkGraphNode[];
  edges: INetworkGraphEdge[];
  allowSearch?: boolean;
}

const NetworkGraph = ({ nodes: propNodes, edges: propEdges, allowSearch = false }: INetworkGraphProps) => {
  const [cyInstance, setCyInstance] = useState<cytoscape.Core | undefined>(undefined);
  const [elements, setElements] = useState<ElementDefinition[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  cytoscape.use(fcose);

  const SAFE_COLORS = [
    "#E69F00", "#56B4E9", "#009E73", "#F0E442",
    "#0072B2", "#D55E00", "#CC79A7", "#999999",
    "#000000", "#F781BF", "#A65628", "#66CCEE",
    "#228833", "#AA4499", "#DDCC77", "#117733",
    "#44AA99", "#882255", "#661100", "#6699CC"
  ];
  const assignedColors = new Map<string, string>();
  const usedColors = new Set<string>();

  const hashStringToInt = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  const generateRandomColour = (existing: Set<string>): string => {
    let color;
    let attempts = 0;
  
    do {
      const hue = Math.floor(Math.random() * 360);
      color = `hsl(${hue}, 65%, 50%)`;
      attempts++;
    } while (existing.has(color) && attempts < 1000);
  
    return color;
  };

  const getColorForString = (label: string): string => {
    if (assignedColors.has(label)) {
      return assignedColors.get(label) || "";
    }
  
    const idx = hashStringToInt(label) % SAFE_COLORS.length;
    const candidate = SAFE_COLORS[idx];
  
    let finalColor = candidate;
    if (usedColors.has(candidate)) {
      finalColor = generateRandomColour(usedColors);
    }
  
    assignedColors.set(label, finalColor);
    usedColors.add(finalColor);
  
    return finalColor;
  };

  const determineNodeShape = (node_type: string) => {
    switch(node_type) {
      case "alertcase":
        return "round-rectangle"
      case "aggregatoralertcase":
        return "round-pentagon"
      default:
        return "ellipse"
    }
  }

  useEffect(() => {
    const nodes = propNodes.map((node) => {
      return {
        ...node,
        data: {
          ...node.data,
          shape: determineNodeShape(node.data.type.toLowerCase())
        },
        style: {
          "background-color": getColorForString(node.data.type),
        },
      };
    });
    
    setElements([...nodes, ...propEdges]);
  }, [propNodes, propEdges]);


  const searchNode = () => {
    if (!allowSearch || !cyInstance || cyInstance.destroyed()){
      return; 
    }

    const term = searchTerm.trim().toLowerCase();
    if (!term){
      cyInstance.nodes().removeClass("highlighted");
      return;
    }

    // clear previous highlights
    cyInstance.nodes().removeClass("highlighted");

    const matchedNodes = cyInstance.nodes().filter((node) => {
      const id = node.id().toLowerCase();
      const label = (node.data("label") || "").toLowerCase();
      return id.includes(term) || label.includes(term);
    });

    if (matchedNodes.length === 0) {
      return;
    }
    matchedNodes.addClass("highlighted");
  };


  const zoomIn = () => {
    if (cyInstance && !cyInstance.destroyed()) {
      cyInstance.zoom(cyInstance.zoom() * 1.2);
    }
  };

  const zoomOut = () => {
    if (cyInstance && !cyInstance.destroyed()) {
      cyInstance.zoom(cyInstance.zoom() * 0.8);
    }
  };

  const resetZoom = () => {
    if (cyInstance && !cyInstance.destroyed()) {
      cyInstance.fit();
    }
  };

  return (
    <div className="networkGraph">
      <div className="networkGraph-container">
        {elements.length > 0 && (
          <CytoscapeComponent 
            className="networkGraph-graph"
            elements={elements}
            layout={{
              name: "fcose",
              padding: 10,
              spacingFactor: 2.5,
              nodeRepulsion: 4500,
              idealEdgeLength: 50,
              gravity: 0.25
              } as cytoscape.LayoutOptions}
            cy={(cy) => {
              setCyInstance(cy);
              cy.minZoom(0.7);
              cy.maxZoom(3);
            }}
            stylesheet={[
              {
                selector: "node",
                style: {
                  label: "data(label)",
                  "text-valign": "top",
                  "text-halign": "center",
                  "text-margin-y": -8,
                  "text-wrap": "wrap",
                  "text-max-width": "80px",
                  "font-size": "12px",
                  color: "#fff",
                  "text-background-opacity": 0.5,
                  "text-background-shape": "roundrectangle",
                  "text-background-padding": "4px",
                  "text-background-color": "#161F33",
                },
              },
              // Base node style
              {
                selector: "node",
                style: {
                  width: 30,
                  height: 30,
                  shape: "data(shape)",
                  "border-width": (ele: cytoscape.NodeSingular) =>
                    ele.data("type") === "alertcase" ? 2 : 0,
                  "border-color": (ele: cytoscape.NodeSingular) =>
                    ele.data("type") === "alertcase" ? "#161F33" : "transparent",
                },
              },
              {
                selector: "edge",
                style: {
                  width: 2,
                  "line-color": "#ccc",
                  "curve-style": "bezier",
                  label: "data(label)",
                  "text-rotation": "autorotate",
                  "text-margin-y": -10,
                  "font-size": 10,
                  color: "#333",
                },
              },
              {
                selector: ".highlighted",
                style: {
                  "border-width": 4,
                  "border-color": "#FF3B3B",
                  "overlay-color": "#FF3B3B",
                  "overlay-opacity": 0.25,
                  "transition-property":
                    "border-width, border-color, overlay-opacity",
                  "transition-duration": "0.25s",
                },
              },
            ]}
          />
        )}
      </div>
      {allowSearch && (
        <div className="networkGraph-search">
          <input
            type="text"
            placeholder="Search node by label..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchNode()}
          />
          <button onClick={searchNode}>Find</button>
        </div>
      )}
      <div className="networkGraph-zoom-controls">
        <button onClick={zoomIn}><CiZoomIn /></button>
        <button onClick={zoomOut}><CiZoomOut /></button>
        <button onClick={resetZoom}><MdOutlineZoomInMap /></button>
      </div>
    </div>
  );
};

export default NetworkGraph;